import { titleCase } from '@nsb/core';
import { fullStop } from './holdWords.ts';
import type { ProcessModel } from './useProcess.ts';

/**
 * Gates and inspections passed on an override that nobody has cleared: open exceptions, first. Then the deviations
 * (design study 21): a step come late or a gate marked done early, waiting on the client to confirm it, declined by
 * them, or confirmed and open until it is done.
 */
export function Exceptions({ model, onSelect }: { model: ProcessModel; onSelect: (id: string) => void }) {
  const open = model.dto.steps.filter((s) => !s.off && ((s.gate?.overrideReason && !s.gate.clearedAt) || (s.inspection?.overrideReason && !s.inspection.clearedAt)));
  // Open, whatever its answer (`DeviationDto.open`): a late step until it is done, a gate marked done early until the rest of its phase is.
  const deviations = model.dto.steps.filter((s) => !s.off && s.deviation?.open);
  if (!open.length && !deviations.length) return null;
  // An archived job's exceptions are kept as they ended (L-19 A): each opened to read, none settled.
  const kept = model.dto.kept === 'whole';
  // While nobody signs in as the client, NSB confirms one for them once they tell it (design study 24, L-20 B).
  const forClient = !kept && model.dto.kind !== 'internal' && model.dto.clientSignIns?.length === 0;
  return (
    <ul className="mb-4 space-y-1 border-l-2 border-alert pl-3">
      {open.map((s) => (
        <li key={s.id} className="text-sm">
          <button type="button" className="text-left" onClick={() => onSelect(s.id)}>
            <span className="text-alert">Open exception:</span> {titleCase(s.title)} was passed on an override: “{s.gate?.overrideReason ?? s.inspection?.overrideReason}”{fullStop(s.gate?.overrideReason ?? s.inspection?.overrideReason)} <span className="link">{kept ? 'Open it' : 'Settle it'}</span>
          </button>
        </li>
      ))}
      {deviations.map((s) => {
        const d = s.deviation!;
        // A gate is done early only once confirmed; waiting or declined, it is not done.
        const what =
          d.kind === 'late'
            ? `${titleCase(s.title)} came late`
            : d.answer === 'confirmed'
              ? `${titleCase(s.title)} was marked done before the rest of its phase`
              : `marking ${titleCase(s.title)} done before the rest of its phase`;
        const head =
          d.answer === 'waiting'
            ? forClient
              ? 'Nobody signs in as the client yet, so it is yours to confirm for them:'
              : 'Waiting on the client to confirm it:'
            : d.answer === 'declined'
              ? 'Declined by the client:'
              : 'Open exception:';
        return (
          <li key={`deviation:${s.id}`} className="text-sm">
            <button type="button" className="text-left" onClick={() => onSelect(s.id)}>
              <span className="text-alert">{head}</span> {what}: “{d.reason}”{fullStop(d.reason)} <span className="link">Open it</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
